import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { ColorSwatchGrid } from "./ColorSwatchGrid";

export const CATEGORIES = ["tshirt", "longsleeve", "hoodie", "crewneck", "polo", "tank", "jacket", "hat", "bag", "other"];

export const emptyGarment = () => ({ name: "", brand: "", style_number: "", category: "tshirt", cost_each: 0, retail_price: 0, sizes: ["S", "M", "L", "XL", "2XL"], colors: [], size_costs: {}, notes: "", is_default: false });

function ColorEditRow({ c, onChange, onDelete, prefix }) {
  const set = (k, v) => onChange({ ...c, [k]: v });
  return (
    <div className="flex flex-wrap items-center gap-1.5 border border-slate-200 rounded-lg p-2 mt-2 bg-slate-50" data-testid={`${prefix}-color-edit`}>
      <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(c.hex) ? c.hex : "#000000"} onChange={(e) => set("hex", e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0 p-0 bg-transparent" data-testid={`${prefix}-color-hex`} />
      <Input value={c.name} onChange={(e) => set("name", e.target.value)} placeholder="Colour name" className="h-8 w-40 text-xs rounded-md" data-testid={`${prefix}-color-name`} />
      <Input value={c.pantone || ""} onChange={(e) => set("pantone", e.target.value)} placeholder="Pantone" className="h-8 w-24 text-xs rounded-md" />
      <Input value={c.max_size || ""} onChange={(e) => set("max_size", e.target.value.toUpperCase())} placeholder="Max size" title="Largest size available in this colour" className="h-8 w-20 text-xs rounded-md" />
      <button type="button" className="ml-auto text-slate-400 hover:text-red-500" onClick={onDelete} data-testid={`${prefix}-color-delete`}><Trash2 size={15} /></button>
    </div>
  );
}

function ColorsEditor({ colors, onChange, prefix }) {
  const [sel, setSel] = useState(null);
  const add = () => { onChange([...colors, { name: "New colour", hex: "#888888", pantone: "", max_size: "" }]); setSel(colors.length); };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs">Colours ({colors.length}) · tap a swatch to edit</Label>
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-[#2495D3]" data-testid={`${prefix}-add-color`} onClick={add}><Plus size={13} className="mr-1" />Colour</Button>
      </div>
      <div className="max-h-64 overflow-y-auto pr-1">
        <ColorSwatchGrid colors={colors} selected={sel} onSelect={(_, i) => setSel(sel === i ? null : i)} prefix={`${prefix}-swatch`} size="sm" />
      </div>
      {sel != null && colors[sel] && (
        <ColorEditRow c={colors[sel]} prefix={prefix} onChange={(v) => onChange(colors.map((c, i) => (i === sel ? v : c)))}
          onDelete={() => { onChange(colors.filter((_, i) => i !== sel)); setSel(null); }} />
      )}
    </div>
  );
}

export function GarmentForm({ g, onChange, prefix = "garment" }) {
  const set = (k, v) => onChange({ ...g, [k]: v });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label className="text-xs">Name</Label><Input data-testid={`${prefix}-name`} value={g.name} onChange={(e) => set("name", e.target.value)} className="rounded-lg mt-1" /></div>
        <div><Label className="text-xs">Brand</Label><Input data-testid={`${prefix}-brand`} value={g.brand || ""} onChange={(e) => set("brand", e.target.value)} className="rounded-lg mt-1" /></div>
        <div><Label className="text-xs">Style #</Label><Input data-testid={`${prefix}-style`} value={g.style_number || ""} onChange={(e) => set("style_number", e.target.value)} className="rounded-lg mt-1" /></div>
        <div>
          <Label className="text-xs">Type</Label>
          <select data-testid={`${prefix}-category`} value={g.category} onChange={(e) => set("category", e.target.value)} className="w-full mt-1 h-9 border border-slate-200 rounded-lg px-2 text-sm bg-white">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div><Label className="text-xs">Cost each (CAD) · what you pay</Label><Input data-testid={`${prefix}-cost`} type="number" step="0.01" value={g.cost_each} onChange={(e) => set("cost_each", e.target.value)} className="rounded-lg mt-1 num" /></div>
        <div><Label className="text-xs">Sale price (CAD) · blank garment</Label><Input data-testid={`${prefix}-retail`} type="number" step="0.01" value={g.retail_price ?? 0} onChange={(e) => set("retail_price", e.target.value)} placeholder="0 = cost × markup" className="rounded-lg mt-1 num" /></div>
        <div className="col-span-2"><Label className="text-xs">Sizes (comma separated)</Label>
          <Input data-testid={`${prefix}-sizes`} value={(g.sizes || []).join(", ")} onChange={(e) => set("sizes", e.target.value.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean))} className="rounded-lg mt-1" /></div>
      </div>
      <ColorsEditor colors={g.colors || []} onChange={(v) => set("colors", v)} prefix={prefix} />
      <div><Label className="text-xs">Notes</Label><Input value={g.notes || ""} onChange={(e) => set("notes", e.target.value)} className="rounded-lg mt-1" /></div>
      <div className="flex items-center justify-between"><Label className="text-xs">Default garment (pre-selected in quotes)</Label><Switch checked={!!g.is_default} onCheckedChange={(v) => set("is_default", v)} /></div>
    </div>
  );
}
